import React, { useState, useEffect } from 'react';
import { XMarkIcon, ShoppingCartIcon, TruckIcon, CheckCircleIcon, CreditCardIcon } from '../icons/HeroIcons';
import { StripePaymentForm } from './StripePaymentForm';
import { SavedCardsPayment } from './SavedCardsPayment';
import { AISHA_API_BASE } from '../../constants';

// Prodigi購入フロー。UIはPrintfulPurchaseModalと完全に同じ5ステップ
// （サイズ・色 → 配送先 → 確認 → 決済 → 完了）。中身のデータ/APIのみProdigi。
interface ProdigiOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  merchandiseId: number | string;   // SuzuriMerchandise.id (product_url='Prodigi')
  sku: string;                       // 作成時に選ばれた既定SKU
  goodsId?: number;                  // GoodsManagement id（バリエーション一覧の取得に使用）
  copies?: number;                   // 未使用（数量はモーダル内で選択）
  imageUrl?: string;                 // 合成印刷画像(プレビュー用)
  goodsName?: string;
  onCompleted?: () => void;
}

// Printfulの Variant に相当（1公開variant）
interface PVariant {
  sku: string;
  label: string;       // 表示名（サイズ等）
  price: number | null; // 販売価格(JPY, 原価×実FX＋上乗せ)
  image?: string;
  available: boolean;
  attrs: Record<string, string>; // 軸→値（多軸の段階選択に使用）
}

interface ShippingAddress {
  name: string;
  email: string;
  phone: string;
  zip: string;
  state_name: string;
  city: string;
  address1: string;
  address2: string;
}

const JAPAN_PREFECTURES = [
  '北海道',
  '青森県', '岩手県', '宮城県', '秋田県', '山形県', '福島県',
  '茨城県', '栃木県', '群馬県', '埼玉県', '千葉県', '東京都', '神奈川県',
  '新潟県', '富山県', '石川県', '福井県', '山梨県', '長野県',
  '岐阜県', '静岡県', '愛知県', '三重県',
  '滋賀県', '京都府', '大阪府', '兵庫県', '奈良県', '和歌山県',
  '鳥取県', '島根県', '岡山県', '広島県', '山口県',
  '徳島県', '香川県', '愛媛県', '高知県',
  '福岡県', '佐賀県', '長崎県', '熊本県', '大分県', '宮崎県', '鹿児島県',
  '沖縄県'
];

// インチ表記（例: 10x10"）にcm換算を併記する（例: 10x10" (25.4×25.4cm)）。
// cm表記(21x30 cm等)や背景等の非サイズ文字列はそのまま。
const withCm = (s: string): string =>
  (s || '').replace(/(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)\s*"/g, (_m, a, b) => {
    const cmA = Math.round(parseFloat(a) * 2.54 * 10) / 10;
    const cmB = Math.round(parseFloat(b) * 2.54 * 10) / 10;
    return `${a}x${b}" (${cmA}×${cmB}cm)`;
  });

const ProdigiOrderModal: React.FC<ProdigiOrderModalProps> = ({
  isOpen,
  onClose,
  userId,
  merchandiseId,
  sku,
  goodsId,
  imageUrl,
  goodsName = 'Prodigi商品',
  onCompleted,
}) => {
  const [currentStep, setCurrentStep] = useState(1); // 1:バリアント 2:配送先 3:確認 4:決済 5:完了
  const [variants, setVariants] = useState<PVariant[]>([]);
  const [selectedVariant, setSelectedVariant] = useState<PVariant | null>(null);
  const [axisList, setAxisList] = useState<string[]>([]);            // 可視軸（例: [背景, サイズ]）
  const [axisOptions, setAxisOptions] = useState<Record<string, string[]>>({}); // 軸→選択肢
  const [shippingAddress, setShippingAddress] = useState<ShippingAddress>({
    name: '', email: '', phone: '', zip: '', state_name: '', city: '', address1: '', address2: ''
  });
  const [saveAddress, setSaveAddress] = useState<boolean>(true);
  const [shippingCost, setShippingCost] = useState(0);
  const [breakdown, setBreakdown] = useState<any>(null); // calculate-shipping / payment-intent の内訳
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [orderResult, setOrderResult] = useState<any>(null);
  const [sandbox, setSandbox] = useState(false); // 既定=本番注文（本番キー使用）。管理者はテスト用にサンドボックスへ切替可
  const [isAdmin, setIsAdmin] = useState(false);
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [productImageUrl, setProductImageUrl] = useState<string | null>(null);
  const [zoomImage, setZoomImage] = useState<string | null>(null); // クリックで拡大プレビュー
  const [savedCards, setSavedCards] = useState<any[]>([]);
  const [useNewCard, setUseNewCard] = useState(false);
  const [loadingVariants, setLoadingVariants] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1); setError(''); setBreakdown(null); setShippingCost(0);
      setPaymentIntentId(null); setClientSecret(null); setUseNewCard(false);
      setOrderResult(null); setQuantity(1);
      fetchVariants();
      fetchSavedAddress();
      fetchSavedCards();
      checkAdminStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isAdmin) setSandbox(false); // 非管理者は常に本番注文（サンドボックス切替は管理者のみ）
  }, [isAdmin]);

  // 数量変更時に送料再計算（配送先ステップ）
  useEffect(() => {
    if (currentStep === 2 && selectedVariant && shippingAddress.zip) {
      calculateShipping();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quantity]);

  const fetchSavedCards = async () => {
    try {
      const r = await fetch(`${AISHA_API_BASE}/stripe/saved-cards/?user_id=${userId}`);
      const d = await r.json();
      if (d.success) setSavedCards(d.payment_methods || d.cards || []);
    } catch (e) { /* noop */ }
  };

  const fetchSavedAddress = async () => {
    try {
      const r = await fetch(`${AISHA_API_BASE}/shipping-address/?user_id=${userId}`);
      const d = await r.json();
      if (d.success && d.has_address && d.address) setShippingAddress({ ...shippingAddress, ...d.address });
    } catch (e) { /* noop */ }
  };

  const saveShippingAddress = async () => {
    if (!saveAddress) return;
    try {
      await fetch(`${AISHA_API_BASE}/shipping-address/save/`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, address: shippingAddress }),
      });
    } catch (e) { /* noop */ }
  };

  const checkAdminStatus = async () => {
    try {
      const r = await fetch(`${AISHA_API_BASE}/users/${userId}/admin-status/`);
      const d = await r.json();
      setIsAdmin(!!d.is_admin);
    } catch (e) { setIsAdmin(false); }
  };

  // Prodigiのバリエーション一覧＋FX＋上乗せから販売価格(JPY)を算出してカード化
  const fetchVariants = async () => {
    setLoadingVariants(true);
    setError('');
    if (!goodsId) {
      // goodsId不明時は作成時SKUだけの1カードで続行
      const fallback: PVariant = { sku, label: goodsName, price: null, image: imageUrl, available: true, attrs: {} };
      setVariants([fallback]);
      setAxisList([]); setAxisOptions({});
      setSelectedVariant(fallback);
      setLoadingVariants(false);
      return;
    }
    try {
      const r = await fetch(`${AISHA_API_BASE}/prodigi/goods/${goodsId}/live/`);
      const d = await r.json();
      if (d && d.success) {
        setProductImageUrl(d.image_url || null);
        const all: any[] = d.variants || [];
        const pubSkus: string[] = d.published_skus || [];
        const pub = pubSkus.length > 0 ? all.filter((v) => pubSkus.includes(v.sku)) : all;
        const fx: Record<string, number> = d.fx || {};
        const margin = Number(d.profit_margin) || 0;
        const axes: Record<string, string[]> = d.axes || {};
        // ラベル用の可視軸（cm/inch重複はcm優先で隠す）
        const activeAxes = Object.keys(axes).filter((k) =>
          pub.some((v) => (v.attrs || {})[k]));
        const hidden = new Set(
          activeAxes.filter((k) => k.includes('(cm)') &&
            activeAxes.some((o) => o !== k && !o.includes('(cm)') &&
              (o.includes('サイズ') || o.toLowerCase().includes('size'))))
        );
        const visAxes = activeAxes.filter((k) => !hidden.has(k));
        const cards: PVariant[] = pub.map((v) => {
          const cost = Number(v.price) || 0;
          const rate = fx[v.currency || ''] || 0;
          const price = rate ? Math.round(cost * rate) + margin : null;
          const vattrs: Record<string, string> = {};
          visAxes.forEach((k) => { if ((v.attrs || {})[k]) vattrs[k] = (v.attrs || {})[k]; });
          const attrLabel = visAxes.map((k) => (v.attrs || {})[k]).filter(Boolean).join(' / ');
          const label = attrLabel || v.size_cm || v.size_in || v.sku;
          return { sku: v.sku, label, price, image: imageUrl || d.image_url, available: true, attrs: vattrs };
        });
        // 軸ごとの選択肢（出現順を保持）
        const opts: Record<string, string[]> = {};
        visAxes.forEach((k) => {
          const seen: string[] = [];
          cards.forEach((c) => { const val = c.attrs[k]; if (val && !seen.includes(val)) seen.push(val); });
          if (seen.length) opts[k] = seen;
        });
        const usedAxes = visAxes.filter((k) => (opts[k] || []).length > 0);
        setVariants(cards);
        setAxisList(usedAxes);
        setAxisOptions(opts);
        const sel = cards.find((c) => c.sku === sku) || cards[0] || null;
        setSelectedVariant(sel);
      } else {
        setError('バリエーション情報の取得に失敗しました');
      }
    } catch (e) {
      setError('エラーが発生しました');
    } finally {
      setLoadingVariants(false);
    }
  };

  const calculateShipping = async () => {
    if (!selectedVariant || !shippingAddress.zip) return;
    setLoading(true);
    try {
      const r = await fetch(`${AISHA_API_BASE}/prodigi/purchase/calculate-shipping/`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          merchandise_id: merchandiseId,
          copies: quantity,
          recipient: { ...shippingAddress, country_code: 'JP' },
          variant_sku: selectedVariant.sku,
        }),
      });
      const d = await r.json();
      if (d.success) {
        setShippingCost(d.shipping_cost || 0);
        setBreakdown(d);
      }
    } catch (e) { /* noop */ }
    finally { setLoading(false); }
  };

  const handleVariantSelect = (v: PVariant) => setSelectedVariant(v);

  // 軸の値を選択（多軸の段階選択）。組合せが存在すればそれに、無ければその値を持つ先頭variantにスナップ
  const selectAxisValue = (axis: string, value: string) => {
    const cur = selectedVariant?.attrs || {};
    const desired = { ...cur, [axis]: value };
    const exact = variants.find((v) => axisList.every((a) => v.attrs[a] === desired[a]));
    if (exact) { setSelectedVariant(exact); return; }
    const snap = variants.find((v) => v.attrs[axis] === value);
    if (snap) setSelectedVariant(snap);
  };

  const handleNextStep = async () => {
    if (currentStep === 1 && selectedVariant) {
      setCurrentStep(2);
    } else if (currentStep === 2) {
      await calculateShipping();
      await saveShippingAddress();
      setCurrentStep(3);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const validateShippingForm = () =>
    shippingAddress.name && shippingAddress.email && shippingAddress.phone &&
    shippingAddress.zip && shippingAddress.state_name && shippingAddress.city && shippingAddress.address1;

  if (!isOpen) return null;

  // 表示用の金額（サーバ内訳を優先、無ければクライアント計算）
  const clientSubtotal = (selectedVariant?.price || 0) * quantity;
  const subtotal = breakdown?.subtotal != null ? Math.floor(breakdown.subtotal) : clientSubtotal;
  const shipping = breakdown?.shipping_cost != null ? Math.floor(breakdown.shipping_cost) : shippingCost;
  const tax = breakdown?.tax != null ? Math.floor(breakdown.tax) : Math.floor((subtotal + shipping) * 0.1);
  const total = breakdown?.total != null ? Math.floor(breakdown.total) : (subtotal + shipping + tax);

  // 注文確定（Prodigi）
  const confirmOrder = async (piId: string | null) => {
    const r = await fetch(`${AISHA_API_BASE}/prodigi/purchase/confirm-payment/`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        payment_intent_id: piId, user_id: userId, sandbox,
        order_data: {
          merchandise_id: merchandiseId,
          copies: quantity,
          recipient: { ...shippingAddress, country_code: 'JP' },
          sku: selectedVariant?.sku,
          total_amount: total,
        },
      }),
    });
    const d = await r.json();
    if (d.success) {
      setOrderResult(d);
      setCurrentStep(5);
      if (onCompleted) onCompleted();
    } else {
      setError(d.error || '注文の確定に失敗しました');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[65] p-4">
      <div
        className="bg-gray-800 rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ヘッダー */}
        <div className="sticky top-0 bg-gray-800 border-b border-gray-700 p-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-white flex items-center">
            <ShoppingCartIcon className="w-6 h-6 mr-2" />
            商品を購入
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* プログレスバー */}
        <div className="px-4 py-3 bg-gray-750">
          <div className="flex items-center justify-between">
            {['サイズ・色', '配送先', '確認', '決済', '完了'].map((label, i) => {
              const n = i + 1;
              return (
                <div key={label} className={`flex-1 text-center ${currentStep >= n ? 'text-indigo-400' : 'text-gray-500'}`}>
                  <div className={`w-8 h-8 mx-auto rounded-full flex items-center justify-center ${currentStep >= n ? 'bg-indigo-600' : 'bg-gray-700'}`}>
                    {n}
                  </div>
                  <span className="text-xs mt-1">{label}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* コンテンツ */}
        <div className="p-4">
          {/* 商品情報 */}
          {currentStep < 5 && (
            <div className="mb-4 p-3 bg-gray-700 rounded-lg flex items-center space-x-3">
              {productImageUrl && (
                <button type="button" onClick={() => setZoomImage(productImageUrl)}
                  className="relative shrink-0 cursor-zoom-in group">
                  <img src={productImageUrl} alt="商品" className="w-16 h-16 object-cover rounded group-hover:ring-2 group-hover:ring-indigo-400" />
                  <span className="absolute -bottom-1 -right-1 text-xs bg-green-800 px-1 rounded">商品</span>
                </button>
              )}
              {imageUrl && (
                <button type="button" onClick={() => setZoomImage(imageUrl)}
                  className="relative shrink-0 cursor-zoom-in group">
                  <img src={imageUrl} alt="プレビュー" className="w-16 h-16 object-cover rounded group-hover:ring-2 group-hover:ring-indigo-400" />
                  <span className="absolute -bottom-1 -right-1 text-xs bg-indigo-800 px-1 rounded">プレビュー</span>
                  <span className="absolute top-0.5 right-0.5 text-[10px] bg-black/60 text-white px-1 rounded">🔍</span>
                </button>
              )}
              <div>
                <h3 className="text-white font-semibold">{goodsName}</h3>
                {selectedVariant && (
                  <p className="text-sm text-gray-400">{withCm(selectedVariant.label)}</p>
                )}
                <p className="text-[11px] text-gray-500 mt-0.5">画像をタップで拡大</p>
              </div>
            </div>
          )}

          {/* ステップ1: バリアント選択 */}
          {currentStep === 1 && (
            <div>
              <h3 className="text-lg font-semibold text-white mb-3">サイズと色を選択</h3>

              {loadingVariants ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mx-auto"></div>
                </div>
              ) : error ? (
                <div className="text-red-400 text-center py-4">{error}</div>
              ) : (
                <>
                  {axisList.length >= 2 ? (
                    /* 多軸: 軸ごとに順番に選択（背景を選ぶ → サイズを選ぶ） */
                    <div className="space-y-4 mb-4">
                      {axisList.map((axis, idx) => (
                        <div key={axis}>
                          <label className="block text-sm font-semibold text-gray-300 mb-2">
                            <span className="inline-flex items-center justify-center w-5 h-5 mr-2 rounded-full bg-indigo-600 text-white text-xs">{idx + 1}</span>
                            {axis} を選択
                          </label>
                          <div className="flex flex-wrap gap-2">
                            {(axisOptions[axis] || []).map((val) => {
                              const active = selectedVariant?.attrs[axis] === val;
                              return (
                                <button
                                  key={val}
                                  onClick={() => selectAxisValue(axis, val)}
                                  className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                                    active
                                      ? 'border-indigo-500 bg-indigo-900/40 text-white'
                                      : 'border-gray-600 bg-gray-700 text-gray-300 hover:border-gray-500'
                                  }`}
                                >
                                  {withCm(val)}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                      {/* 選択中バリエーションのプレビュー＋価格 */}
                      {selectedVariant && (
                        <div className="flex items-center justify-between p-3 bg-gray-700 rounded-lg border border-indigo-700/50">
                          <div className="flex items-center space-x-3">
                            {selectedVariant.image && (
                              <img src={selectedVariant.image} alt={selectedVariant.label} className="w-14 h-14 object-cover rounded bg-white" />
                            )}
                            <div>
                              <div className="text-xs text-gray-400">選択中</div>
                              <div className="font-medium text-white">{withCm(selectedVariant.label)}</div>
                            </div>
                          </div>
                          <div className="text-lg font-bold text-indigo-300">
                            {selectedVariant.price != null ? `¥${selectedVariant.price.toLocaleString()}` : '—'}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    /* 単軸: カードグリッド（Printfulと同じ） */
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                      {variants.map((variant) => (
                        <button
                          key={variant.sku}
                          onClick={() => handleVariantSelect(variant)}
                          disabled={!variant.available}
                          className={`p-3 rounded-lg border-2 transition-all text-left ${
                            selectedVariant?.sku === variant.sku
                              ? 'border-indigo-500 bg-indigo-900/30'
                              : 'border-gray-600 bg-gray-700 hover:border-gray-500'
                          } ${!variant.available ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <div className="flex items-center space-x-3">
                            {variant.image && (
                              <img src={variant.image} alt={variant.label} className="w-16 h-16 object-cover rounded bg-white" />
                            )}
                            <div className="flex-1 flex justify-between items-start">
                              <div>
                                <div className="font-medium text-white">{withCm(variant.label)}</div>
                                <div className="text-lg font-bold text-indigo-300 mt-1">
                                  {variant.price != null ? `¥${variant.price.toLocaleString()}` : '—'}
                                </div>
                              </div>
                              {!variant.available && <span className="text-xs text-red-400">在庫なし</span>}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* 数量選択 */}
                  {selectedVariant && (
                    <div className="mt-4 p-3 bg-gray-700 rounded-lg">
                      <label className="block text-sm text-gray-400 mb-2">数量</label>
                      <div className="flex items-center space-x-3">
                        <button onClick={() => setQuantity(Math.max(1, quantity - 1))}
                          className="w-8 h-8 rounded bg-gray-600 hover:bg-gray-500 text-white font-bold transition-colors">-</button>
                        <input type="number" value={quantity}
                          onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                          className="w-20 px-3 py-1 bg-gray-800 text-white text-center rounded border border-gray-600 focus:border-indigo-500 focus:outline-none"
                          min="1" max="10" />
                        <button onClick={() => setQuantity(Math.min(10, quantity + 1))}
                          className="w-8 h-8 rounded bg-gray-600 hover:bg-gray-500 text-white font-bold transition-colors">+</button>
                        <span className="text-sm text-gray-400">個</span>
                      </div>
                      <div className="mt-3 pt-3 border-t border-gray-600">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-400">小計：</span>
                          <span className="text-lg font-bold text-white">
                            {selectedVariant.price != null ? `¥${(selectedVariant.price * quantity).toLocaleString()}` : '—'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ステップ2: 配送先入力 */}
          {currentStep === 2 && (
            <div>
              <h3 className="text-lg font-semibold text-white mb-3 flex items-center">
                <TruckIcon className="w-5 h-5 mr-2" />
                配送先情報
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">お名前 *</label>
                    <input type="text" value={shippingAddress.name}
                      onChange={(e) => setShippingAddress({ ...shippingAddress, name: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-indigo-500 focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">メールアドレス *</label>
                    <input type="email" value={shippingAddress.email}
                      onChange={(e) => setShippingAddress({ ...shippingAddress, email: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-indigo-500 focus:outline-none" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">電話番号 *</label>
                    <input type="tel" value={shippingAddress.phone}
                      onChange={(e) => setShippingAddress({ ...shippingAddress, phone: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-indigo-500 focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">郵便番号 *</label>
                    <input type="text" value={shippingAddress.zip}
                      onChange={(e) => setShippingAddress({ ...shippingAddress, zip: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-indigo-500 focus:outline-none" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">都道府県 *</label>
                    <select value={shippingAddress.state_name}
                      onChange={(e) => setShippingAddress({ ...shippingAddress, state_name: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-indigo-500 focus:outline-none appearance-none cursor-pointer">
                      <option value="">選択してください</option>
                      {JAPAN_PREFECTURES.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">市区町村 *</label>
                    <input type="text" value={shippingAddress.city}
                      onChange={(e) => setShippingAddress({ ...shippingAddress, city: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-indigo-500 focus:outline-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">住所1 *</label>
                  <input type="text" value={shippingAddress.address1}
                    onChange={(e) => setShippingAddress({ ...shippingAddress, address1: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-indigo-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">住所2（マンション名など）</label>
                  <input type="text" value={shippingAddress.address2}
                    onChange={(e) => setShippingAddress({ ...shippingAddress, address2: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-indigo-500 focus:outline-none" />
                </div>
                <div className="mt-4 p-3 bg-gray-600 rounded-lg">
                  <label className="flex items-center cursor-pointer">
                    <input type="checkbox" checked={saveAddress}
                      onChange={(e) => setSaveAddress(e.target.checked)}
                      className="w-4 h-4 text-indigo-600 bg-gray-700 border-gray-600 rounded focus:ring-indigo-500" />
                    <span className="ml-2 text-white">この住所を保存して次回から入力を省略する</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* ステップ3: 確認 */}
          {currentStep === 3 && (
            <div>
              <h3 className="text-lg font-semibold text-white mb-3">注文内容の確認</h3>

              {/* 価格内訳 */}
              <div className="mb-4 p-3 bg-gray-700 rounded-lg">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-gray-400">
                    <span>商品価格 {quantity > 1 && selectedVariant?.price != null && `(${selectedVariant.price.toLocaleString()}円 × ${quantity}個)`}</span>
                    <span>¥{subtotal.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-gray-400">
                    <span>送料 {quantity > 1 && `(${quantity}個分)`}</span>
                    <span>¥{shipping.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-gray-400">
                    <span>消費税（10%）</span>
                    <span>¥{tax.toLocaleString()}</span>
                  </div>
                  <div className="border-t border-gray-600 pt-2 flex justify-between font-semibold text-white">
                    <span>合計</span>
                    <span className="text-lg">¥{total.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* 配送先情報 */}
              <div className="mb-4 p-3 bg-gray-700 rounded-lg">
                <h4 className="text-sm font-semibold text-gray-300 mb-2">配送先</h4>
                <div className="text-sm text-gray-400 space-y-1">
                  <p>{shippingAddress.name}</p>
                  <p>〒{shippingAddress.zip}</p>
                  <p>{shippingAddress.state_name} {shippingAddress.city}</p>
                  <p>{shippingAddress.address1}</p>
                  {shippingAddress.address2 && <p>{shippingAddress.address2}</p>}
                  <p>TEL: {shippingAddress.phone}</p>
                  <p>Email: {shippingAddress.email}</p>
                </div>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-900/50 border border-red-500 rounded text-red-400 text-sm">{error}</div>
              )}

              <div className="mb-4 p-3 bg-blue-900/50 border border-blue-500 rounded text-blue-300 text-sm">
                次のステップでクレジットカード情報を入力して決済を行います。
              </div>

              <button
                onClick={async () => {
                  setLoading(true); setError('');
                  try {
                    const response = await fetch(`${AISHA_API_BASE}/prodigi/purchase/payment-intent/`, {
                      method: 'POST', headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        user_id: userId,
                        merchandise_id: merchandiseId,
                        copies: quantity,
                        shipping_cost: shippingCost,
                        recipient: { ...shippingAddress, country_code: 'JP' },
                        variant_sku: selectedVariant?.sku,
                      }),
                    });
                    const data = await response.json();
                    if (data.success) {
                      setPaymentIntentId(data.payment_intent_id);
                      setClientSecret(data.client_secret);
                      if (data.breakdown) setBreakdown({ ...breakdown, ...data.breakdown });
                      setCurrentStep(4);
                    } else {
                      setError(data.error || 'お支払い準備に失敗しました');
                    }
                  } catch (err) {
                    setError('エラーが発生しました');
                  } finally {
                    setLoading(false);
                  }
                }}
                disabled={loading}
                className={`w-full py-3 rounded font-medium transition-colors ${
                  loading ? 'bg-gray-600 text-gray-400 cursor-not-allowed' : 'bg-indigo-600 text-white hover:bg-indigo-700'
                }`}
              >
                {loading ? '処理中...' : '決済へ進む'}
              </button>
            </div>
          )}

          {/* ステップ4: 決済 */}
          {currentStep === 4 && clientSecret && (
            <div>
              <h3 className="text-lg font-semibold text-white mb-3 flex items-center">
                <CreditCardIcon className="w-5 h-5 mr-2" />
                クレジットカード決済
              </h3>

              {savedCards.length > 0 && !useNewCard ? (
                <SavedCardsPayment
                  userId={userId}
                  totalAmount={total}
                  onSelectCard={async (cardId: string) => {
                    setLoading(true); setError('');
                    try {
                      const r = await fetch(`${AISHA_API_BASE}/stripe/payment-with-saved-card/`, {
                        method: 'POST', headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ user_id: userId, payment_method_id: cardId, amount: total, credit_amount: 0 }),
                      });
                      const d = await r.json();
                      if (d.success) { await confirmOrder(d.payment_intent_id); }
                      else { setError(d.error || '決済に失敗しました'); }
                    } catch (e) { setError('決済処理中にエラーが発生しました'); }
                    finally { setLoading(false); }
                  }}
                  onPaymentSuccess={async () => {
                    setLoading(true);
                    try { await confirmOrder(paymentIntentId); }
                    catch (e) { setError('エラーが発生しました'); }
                    finally { setLoading(false); }
                  }}
                  onPaymentError={(e: string) => setError(e)}
                  onUseNewCard={() => setUseNewCard(true)}
                  onCancel={() => setCurrentStep(3)}
                  savedCards={savedCards}
                  onCardsUpdate={fetchSavedCards}
                />
              ) : (
                <>
                  {savedCards.length > 0 && (
                    <button onClick={() => setUseNewCard(false)}
                      className="mb-3 text-indigo-400 hover:text-indigo-300 text-sm flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                      保存済みカードに戻る
                    </button>
                  )}
                  <StripePaymentForm
                    userId={userId}
                    chargeAmount={total}
                    creditAmount={0}
                    existingClientSecret={clientSecret}
                    existingPaymentIntentId={paymentIntentId || undefined}
                    defaultPostalCode={shippingAddress.zip}
                    onSuccess={async () => {
                      setLoading(true);
                      try { await confirmOrder(paymentIntentId); }
                      catch (e) { setError('エラーが発生しました'); }
                      finally { setLoading(false); }
                    }}
                    onError={(e: string) => setError(e)}
                    onCancel={() => setCurrentStep(3)}
                  />
                </>
              )}
            </div>
          )}

          {/* ステップ5: 完了 */}
          {currentStep === 5 && orderResult && (
            <div className="text-center py-8">
              <CheckCircleIcon className="w-16 h-16 mx-auto text-green-500 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">
                {orderResult.sandbox ? 'テスト注文が完了しました！' : '注文が送信されました！'}
              </h3>
              <div className="mt-4 p-4 bg-gray-700 rounded-lg text-left max-w-md mx-auto">
                <h4 className="text-sm font-semibold text-gray-300 mb-2">Prodigi注文詳細</h4>
                <div className="space-y-2 text-sm text-gray-400">
                  <p>注文ID: {orderResult.order?.id}</p>
                  <p>Prodigi ID: {orderResult.order?.prodigi_id}</p>
                  <p>ステータス: {orderResult.order?.status}</p>
                </div>
              </div>
              {orderResult.sandbox && (
                <div className="mt-4 p-4 bg-yellow-900/50 border border-yellow-600 rounded-lg max-w-md mx-auto">
                  <p className="text-yellow-400 text-sm">
                    これはサンドボックス（テスト）注文です。Prodigi側で実際の製造・発送は行われません。
                  </p>
                </div>
              )}
              <button onClick={onClose}
                className="mt-6 px-6 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors">
                閉じる
              </button>
            </div>
          )}
        </div>

        {/* フッター */}
        {currentStep < 4 && (
          <div className="sticky bottom-0 bg-gray-800 border-t border-gray-700 p-4 flex justify-between items-center">
            {currentStep > 1 && (
              <button onClick={handlePreviousStep}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500 transition-colors">戻る</button>
            )}
            <div className="ml-auto">
              {currentStep < 3 && (
                <button
                  onClick={handleNextStep}
                  disabled={(currentStep === 1 && !selectedVariant) || (currentStep === 2 && !validateShippingForm())}
                  className={`px-6 py-2 rounded font-medium transition-colors ${
                    ((currentStep === 1 && selectedVariant) || (currentStep === 2 && validateShippingForm()))
                      ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                      : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  次へ
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* クリック拡大プレビュー（ライトボックス） */}
      {zoomImage && (
        <div className="fixed inset-0 z-[80] bg-black/85 flex items-center justify-center p-4"
          onClick={() => setZoomImage(null)}>
          <img src={zoomImage} alt="拡大プレビュー"
            className="max-w-full max-h-full object-contain rounded shadow-2xl bg-white" />
          <button onClick={() => setZoomImage(null)}
            className="absolute top-4 right-4 text-white bg-black/50 rounded-full p-2 hover:bg-black/70">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>
      )}
    </div>
  );
};

export default ProdigiOrderModal;
